/* eslint-disable max-lines */
import { useArtwork, useArtworkForm, useClearManualEdits } from "@design-lab/entities/artwork";
import {
  designProjectPlacementIdsAtom,
  type DesignLabPlacementKey,
  isSessionFinished,
  pinningSessionsAtom,
  useCancelPinningSession
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinningInstructionsRemoved,
  trackPinsConfirmationAddToCartClicked,
  trackPinsConfirmationDeleteClicked,
  trackPinsConfirmationModalClosed,
  trackPinsConfirmationModalShown
} from "@design-lab/shared/lib/analytics";
import type { ArtworkForm, Side } from "@design-lab/shared/types";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";

export const reviewPinningModalOpenAtom = atom<boolean>(false);

// Atom to store the add-to-cart callback
const addToCartCallbackAtom = atom<(() => void | Promise<void>) | null>(null);

export type CompletedSessionInfo = {
  designProjectPlacementId: string;
  artworkId: string;
  sessionId: string;
  summary: string | null;
  side: "front" | "back";
  artworkImageUrl: string | undefined;
};

/**
 * Get the enhanced image URL based on user settings (removeBackground, enhancementTier)
 */
function getEnhancedArtworkUrl(
  artwork: ReturnType<ReturnType<typeof useArtwork>["getArtwork"]>,
  info: ArtworkForm["info"]["front"] | ArtworkForm["info"]["back"] | undefined
): string | undefined {
  if (!artwork) return undefined;

  const removeBackground = info?.removeBackground ?? true;
  const enhancementTier = info?.enhancementTier ?? "full_enhancements";
  const canRemoveBackground = artwork.canRemoveBg ?? true;

  // When a manual edit URL exists, always use it regardless of tier
  if (artwork.manualEditUrl) {
    return artwork.manualEditUrl;
  }

  // For print_original tier, use only originalImageUrl to match backend behavior (artwork.original_image)
  // This ensures pin coordinates calculated from displayed image match backend image dimensions
  if (enhancementTier === "print_original") {
    return artwork.originalImageUrl;
  }

  // For full_enhancements, respect removeBackground setting
  if (removeBackground && canRemoveBackground) {
    return (
      artwork.noBgUrl || artwork.noBgNoTrimUrl || artwork.withBgUrl || artwork.originalImageUrl
    );
  }

  // Default: use withBgUrl or original
  return artwork.withBgUrl || artwork.originalImageUrl;
}

export function useReviewPinningModal() {
  const { form } = useArtworkForm();
  const { getArtwork } = useArtwork();
  const [isModalOpen, setIsModalOpen] = useAtom(reviewPinningModalOpenAtom);
  const [addToCartCallback, setAddToCartCallback] = useAtom(addToCartCallbackAtom);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const pinningSessions = useAtomValue(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setPinningSessions = useSetAtom(pinningSessionsAtom);
  const { mutate: cancelSession } = useCancelPinningSession();
  const clearManualEdits = useClearManualEdits();
  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  // Get artwork IDs from the form
  const frontArtworkId =
    artworkForm?.front?.artworkId || artworkForm?.frontLeftChest?.artworkId || "";
  const backArtworkId = artworkForm?.back?.artworkId || "";
  const selectedFrontPlacement =
    (artworkForm as any)?.selectedFrontPlacement === "frontLeftChest" ? "frontLeftChest" : "front";
  const frontPlacementKey = selectedFrontPlacement as DesignLabPlacementKey;
  const frontPlacementId = designProjectPlacementIds[frontPlacementKey] || "";
  const backPlacementId = designProjectPlacementIds.back || "";

  // Get completed sessions for each side
  const completedSessions = useMemo(() => {
    const sessions: CompletedSessionInfo[] = [];

    if (frontArtworkId && frontPlacementId) {
      const session = pinningSessions[frontPlacementId];
      if (session && isSessionFinished(session.state)) {
        const artwork = getArtwork(frontArtworkId);
        const frontInfo = artworkForm?.info?.front;
        sessions.push({
          designProjectPlacementId: frontPlacementId,
          artworkId: frontArtworkId,
          sessionId: session.sessionId,
          summary: session.summary,
          side: "front",
          artworkImageUrl: getEnhancedArtworkUrl(artwork, frontInfo)
        });
      }
    }

    if (backArtworkId && backPlacementId) {
      const session = pinningSessions[backPlacementId];
      if (session && isSessionFinished(session.state)) {
        const artwork = getArtwork(backArtworkId);
        const backInfo = artworkForm?.info?.back;
        sessions.push({
          designProjectPlacementId: backPlacementId,
          artworkId: backArtworkId,
          sessionId: session.sessionId,
          summary: session.summary,
          side: "back",
          artworkImageUrl: getEnhancedArtworkUrl(artwork, backInfo)
        });
      }
    }

    return sessions;
  }, [
    backArtworkId,
    backPlacementId,
    frontArtworkId,
    frontPlacementId,
    getArtwork,
    artworkForm?.info,
    pinningSessions
  ]);

  const hasCompletedSessions = completedSessions.length > 0;

  const openModal = useCallback(
    (onConfirmAddToCart?: () => void | Promise<void>) => {
      setAddToCartCallback(() => onConfirmAddToCart || null);
      setIsModalOpen(true);
      trackPinsConfirmationModalShown({
        ...(frontArtworkId ? { frontArtworkId } : {}),
        ...(backArtworkId ? { backArtworkId } : {})
      });
    },
    [setIsModalOpen, setAddToCartCallback, frontArtworkId, backArtworkId]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setAddToCartCallback(null);
    trackPinsConfirmationModalClosed();
  }, [setIsModalOpen, setAddToCartCallback]);

  const handleDelete = useCallback(
    (session: CompletedSessionInfo) => {
      // Track modal-specific event
      trackPinsConfirmationDeleteClicked({
        artworkId: session.artworkId,
        side: session.side
      });

      // Call API to delete the session, then update state on success
      cancelSession(
        { sessionId: session.sessionId, artworkId: session.artworkId },
        {
          onSuccess: () => {
            // Track instructions removed
            trackDesignLabPinningInstructionsRemoved({
              artworkId: session.artworkId,
              sessionId: session.sessionId
            });

            // Remove session from state
            setPinningSessions(prev => {
              const next = { ...prev };
              delete next[session.designProjectPlacementId];
              return next;
            });

            // Clear jiffyDesignServices and enhancementRequestNote for the side
            const side: Side = session.side === "back" ? "back" : "front";
            form.setValue(`info.${side}.jiffyDesignServices`, false);
            form.setValue(`info.${side}.enhancementRequestNote`, "");

            // Reset manual edit state (clears manualEditUrl and syncs with backend)
            clearManualEdits(side);
          }
        }
      );
    },
    [cancelSession, setPinningSessions, form, clearManualEdits]
  );

  const handleAddToCart = useCallback(async () => {
    trackPinsConfirmationAddToCartClicked();

    // Call the stored callback to proceed with add to cart
    if (!addToCartCallback) {
      setIsModalOpen(false);
      return;
    }
    const callback = addToCartCallback;
    setAddToCartCallback(null);
    setIsAddingToCart(true);
    try {
      await callback();
    } finally {
      setIsAddingToCart(false);
      setIsModalOpen(false);
    }
  }, [addToCartCallback, setIsModalOpen, setAddToCartCallback]);

  return {
    isModalOpen,
    isAddingToCart,
    completedSessions,
    hasCompletedSessions,
    openModal,
    closeModal,
    handleDelete,
    handleAddToCart
  };
}
